import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CalendarDays, ClipboardCheck, Flame, Heart } from 'lucide-react';

export default function FacilitatorDashboard() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [heatCompletions, setHeatCompletions] = useState([]);
  const [herCompletions, setHerCompletions] = useState([]);
  const [heatSessions, setHeatSessions] = useState([]);
  const [herSessions, setHerSessions] = useState([]);

  useEffect(() => {
    api.get('/heat/completions').then(r => setHeatCompletions(r.data || [])).catch(() => {});
    api.get('/her/completions').then(r => setHerCompletions(r.data || [])).catch(() => {});
    api.get('/heat/sessions').then(r => setHeatSessions(r.data || [])).catch(() => {});
    api.get('/her/sessions').then(r => setHerSessions(r.data || [])).catch(() => {});
  }, []);

  const pendingHeat = useMemo(() => heatCompletions.filter(c => c.approval_status === 'pending_facilitator').length, [heatCompletions]);
  const pendingHer = useMemo(() => herCompletions.filter(c => c.approval_status === 'pending_facilitator').length, [herCompletions]);
  const upcomingSessions = useMemo(() => {
    const allSessions = [...heatSessions.map(s => ({ ...s, program: 'HEAT' })), ...herSessions.map(s => ({ ...s, program: 'HER' }))];
    return allSessions
      .filter(s => s.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 4);
  }, [heatSessions, herSessions]);

  return (
    <div className="space-y-6" data-testid="facilitator-dashboard">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">H.E.A.T. / H.E.R. Facilitator</p>
          <h1 className="text-2xl font-semibold">Facilitator Command Center</h1>
          <p className="text-sm text-muted-foreground">Track curriculum delivery, attendance, and module approvals.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-full" onClick={() => navigate('/heat/modules')} data-testid="facilitator-heat-modules">
            <Flame className="w-4 h-4 mr-2" /> HEAT Modules
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/her/modules')} data-testid="facilitator-her-modules">
            <Heart className="w-4 h-4 mr-2" /> HER Modules
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="facilitator-pending-heat">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-orange-600" /> HEAT Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{pendingHeat}</p>
            <p className="text-xs text-muted-foreground">Modules waiting for facilitator sign-off</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="facilitator-pending-her">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-rose-600" /> HER Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{pendingHer}</p>
            <p className="text-xs text-muted-foreground">Modules waiting for facilitator sign-off</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="facilitator-upcoming-sessions">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-sky-600" /> Upcoming Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{upcomingSessions.length}</p>
            <p className="text-xs text-muted-foreground">Across HEAT + HER</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm rounded-2xl" data-testid="facilitator-session-list">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Next sessions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {upcomingSessions.length ? upcomingSessions.map(s => (
            <div key={s.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <div>
                <p className="text-sm font-semibold">{s.title}</p>
                <p className="text-xs text-muted-foreground">{s.program} • {s.date} {s.time}</p>
              </div>
              <Button variant="outline" className="rounded-full" size="sm" onClick={() => navigate(s.program === 'HEAT' ? '/heat/attendance' : '/her/attendance')} data-testid={`facilitator-session-view-${s.id}`}>
                View Attendance
              </Button>
            </div>
          )) : (
            <p className="text-sm text-muted-foreground">No upcoming sessions scheduled.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
